import React, { ChangeEvent, FormEvent, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import styled from 'styled-components';
import { useNavigate } from 'react-router';
import find from 'lodash/find';

import BirthdayField from './BirthdayField';
import { useContactsContext } from '../../contexts/ContactsContext';
import { EditableField } from '../../types';
import { FormSubtitle, Separator } from '../styled';
import { ContactFormForm, FieldCol, SubmitButtonGroup } from '../styled/ContactsForm';
import { ContactsButton, ContactsButtonSecondary } from '../styled/ContactsButton';
import { StyledFormControl } from '../styled/Inputs';
import NameFields from './NameFields';
import PhoneFields from './PhoneFields';
import EmailFields from './EmailFields';
import AddressFields from './AddressFields';
import ThirdPartyIdFields from './ThirdPartyIdFields';
import { UAppThirdPartyId } from '../../models/AugmentedClasses';
import { UAppContact } from '../../models/UAppContact';

import {
  TAppContact,
  TAppDate,
  TAppEmail,
  TAppName,
  TAppPhone,
  TAppProfessionalInfo,
  TAppThirdPartyId,
  TAppThirdPartyIdSource
} from 'TProtocol/common/models';

interface Props {
  onSubmit: (contact: UAppContact) => void;
  nameRequired?: boolean;
  simple?: boolean;
  popup: boolean;
  submitLabel?: IUContactFormSubmitLabel;
}

export enum IUContactFormSubmitLabel {
  Save = 1,
  Submit
}

export default function ContactForm(props: Props) {
  const navigate = useNavigate();
  const contactsContext = useContactsContext();
  const contact = contactsContext.contactToEdit;

  const handleStopSyncing = async () => {
    contactsContext.stopSyncing();
  };

  const handleContinueSyncing = async () => {
    await contactsContext.continueSyncing();
  };

  // STOP SYNCING ON LOAD FOR EDITS
  useEffect(() => {
    if (contact?.contactId) {
      void handleStopSyncing();
    }
  }, []);

  // RETURN SYNCING
  useEffect(() => {
    return () => {
      void handleContinueSyncing();
      contactsContext.setContactToEdit(undefined);
    };
  }, []);

  if (!contact) {
    return null;
  }

  // A = fieldName in TAppContact (e.g. "primaryProfessionalInfo")
  // K = key in the field class to set (e.g. "jobTitle")
  // V = value of the key to set (always string, but needs to be set for narrowing to work)
  const handleChange = <A extends EditableField, K extends keyof Required<TAppContact>[A], V extends Required<TAppContact>[A][K]>(
    value: V,
    fieldType: new () => Required<TAppContact>[A],
    fieldName: A,
    fieldAttribute: K,
    secondaryFieldAttribute?: K,
  ) => {
    const nonOptionalContact: Required<TAppContact> = contact as Required<TAppContact>;

    if (!nonOptionalContact[fieldName]) {
      nonOptionalContact[fieldName] = new fieldType();
    }

    const contactField = nonOptionalContact[fieldName];

    if (contactField) {
      contactField[fieldAttribute] = value;
    }
    if (secondaryFieldAttribute && secondaryFieldAttribute !== '') {
      contactField[secondaryFieldAttribute] = value;
    }

    contactsContext.setContactToEdit(nonOptionalContact);
  };

  const onChange = (contact: UAppContact) => {
    contactsContext.setContactToEdit(contact);
  };

  const handleBirthdayChange = (value: string, fieldAttribute: 'month' | 'day' | 'year') => {
    if (!contact.birthday) {
      contact.birthday = new TAppDate();
    }
    contact.birthday[fieldAttribute] = value;
    if (!contact.birthday.month || contact.birthday.month.length <= 0) {
      if (contact.birthday.day) {
        contact.birthday.day = '';
      }
    }

    contactsContext.setContactToEdit(contact);
  };

  // Remove any "other" devices that were initialized but left empty
  const removeEmptyDevices = () => {
    contact.otherEmails = contact.otherEmails?.filter((el: TAppEmail) => el.emailAddress);
    contact.otherPhones = contact.otherPhones?.filter((el: TAppPhone) => el.phoneNumber);
    contact.otherThirdPartyIds = contact.otherThirdPartyIds?.filter((el: TAppThirdPartyId) => el.value);
  };

  const addThirdPartyIdField = () => {
    const newThirdPartyId = new UAppThirdPartyId();

    if (!contact.otherThirdPartyIds) {
      contact.otherThirdPartyIds = [];
    }

    [
      TAppThirdPartyIdSource.TWITTER, TAppThirdPartyIdSource.INSTAGRAM, TAppThirdPartyIdSource.FACEBOOK_MESSENGER,
      TAppThirdPartyIdSource.FACEBOOK, TAppThirdPartyIdSource.LINKEDIN, TAppThirdPartyIdSource.VENMO,
      TAppThirdPartyIdSource.URL, TAppThirdPartyIdSource.WHATSAPP, TAppThirdPartyIdSource.SNAPCHAT,
      TAppThirdPartyIdSource.YELP
    ].forEach((source) => {
      if (newThirdPartyId.source === undefined && !find(contact.otherThirdPartyIds, { source })) {
        newThirdPartyId.source = source;
      }
    });

    contact.otherThirdPartyIds.push(newThirdPartyId);

    contactsContext.setContactToEdit(contact);

    return newThirdPartyId;
  };

  // const onThirdPartyIdDelete = (thirdPartyId: TAppThirdPartyId) => {
  //   contact.otherThirdPartyIds = reject(contact.otherThirdPartyIds, thirdPartyId);
  //   contactsContext.setContactToEdit(contact);
  // };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!contact.contactId) {
      contact.contactId = uuidv4();
    }

    removeEmptyDevices();

    if (props.onSubmit) {
      props.onSubmit(contact);
    } else {
      await contactsContext.onEditComplete(contact);
      navigate('/contacts', { replace: true });
    }
  };

  const onCancel = () => {
    contactsContext.setContactToEdit(undefined);
  };

  let jobTitle = '';
  let companyName = '';
  if (contact.primaryProfessionalInfo) {
    jobTitle = contact.primaryProfessionalInfo.jobTitle ?? '';
    companyName = contact.primaryProfessionalInfo.companyName ?? '';
  }

  return (
    <ContactFormContainer data-contact-id={contact.contactId}>
      <ContactFormForm data-form-form={true} onSubmit={handleSubmit}>

        {props.simple ? <Row data-contact-id={contact.contactId}>
          <FieldCol>
            <StyledFormControl
              required={props.nameRequired === true}
              autoComplete="name"
              name="name"
              placeholder="Full Name"
              defaultValue={contact.name?.fullName}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleChange(e.target.value, TAppName, 'name', 'fullName')
              }
            />
          </FieldCol>
        </Row> :
          <NameFields contact={contact} onChange={onChange}/>
        }
        <BirthdayField
          handleBirthdayChange={handleBirthdayChange}
          birthday={contact.birthday}
          label="Birthday"
        />

        <Separator/>
        <PhoneFields contact={contact} onChange={onChange}/>

        <Separator/>
        <EmailFields contact={contact} onChange={onChange}/>

        <Separator/>
        <AddressFields simple={props.simple} contact={contact} onChange={onChange}/>

        <Separator/>
        <FormSubtitle>Professional Info</FormSubtitle>

        <Row>
          <FieldCol>
            <StyledFormControl
              autoComplete="do-not-autocomplete"
              defaultValue={companyName}
              placeholder="Company Name"
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleChange(e.target.value, TAppProfessionalInfo, 'primaryProfessionalInfo', 'companyName')
              }
            />
          </FieldCol>
        </Row>
        <Row>
          <FieldCol>
            <StyledFormControl
              autoComplete="do-not-autocomplete"
              defaultValue={jobTitle}
              placeholder="Job Title"
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleChange(e.target.value, TAppProfessionalInfo, 'primaryProfessionalInfo', 'jobTitle')
              }/>
          </FieldCol>
        </Row>

        <Separator/>
        <Row>
          <FieldCol>
            <ThirdPartyIdFields contact={contact} onChange={onChange}/>
          </FieldCol>
          <OtherHeader onClick={addThirdPartyIdField}>
            <OtherPlus>+</OtherPlus> Social Profile
          </OtherHeader>
        </Row>
        <SubmitButtonGroup>
          <ContactsButton hover={true} type="submit">
            { props.submitLabel === IUContactFormSubmitLabel.Submit ? 'Submit' : 'Save' }
          </ContactsButton>
          {props.popup &&
            <ContactsButtonSecondary onClick={onCancel}>
              Cancel
            </ContactsButtonSecondary>}
        </SubmitButtonGroup>

      </ContactFormForm>
    </ContactFormContainer>
  );

}

const ContactFormContainer = styled.div`
  ${Separator} {
    margin-top: 8px;
  }
`;

const OtherHeader = styled(Form.Group)`
  margin-bottom: 10px;
`;

export const OtherPlus = styled.span`
  display: inline-block;
  width: 25px;
  height: 25px;
  background: linear-gradient(#009bdf, #009bdf), linear-gradient(#009bdf, #009bdf), #FFF;
  background-position: center;
  background-size: 50% 2px, 2px 50%;
  background-repeat: no-repeat;
  border-radius: 6px;
  border: solid 1px #009bdf;
  color: transparent;
  cursor: pointer;
  margin-right: 5px;
`;
